import {Application, SinglePageApplication} from '../../types/project/Application';
import {StreamingDevice} from '../../types/deployment/StreamingDeviceResponse';
import {Deployment} from '../../types/deployment/DeploymentResponse';
import {Parameter} from '../../types/model/framework/Parameter';
import {InferenceAppConfig} from './useInferenceAppTask';
import {InferenceAppExecution, InferenceAppForm} from './InferenceAppTypes';
import {StartInferenceRequest} from '../../types/deployment/InferenceRequest';
import {VideoStreamSubject} from '../TestDeployView/HlsVideoStream';
import {ApplicationDetails} from '../../types/project/ApplicationDetails';

export type SubmitRequest = {
  spa: SinglePageApplication;
  application: Application;
  applicationDetails: ApplicationDetails;
  configuredParameters: Parameter[];
  streamingDevices: StreamingDevice[];
  streamSubject: VideoStreamSubject;
  values: InferenceAppForm;
  handleInferenceStart: (config: InferenceAppConfig) => void;
  handleEdgeInferenceStart: (config: StartInferenceRequest) => void;
  setExecutionContext: (c: InferenceAppExecution) => void;
  onError: (e: Error) => void;
};

export function getSubmitFunction(request: SubmitRequest): (r: SubmitRequest) => void {
  if (request.applicationDetails.supportsSinglePageApplications) {
    if (request.spa.input.type === 'BATCH') {
      if (request.application?.runtimeType === 'ONNX_RUNTIME_WEB') {
        return submitBatchClientSide;
      } else {
        return submitBatchServerSide;
      }
    } else if (request.spa.input.type === 'STREAM') {
      if (request.application?.runtimeType === 'ONNX_RUNTIME_WEB') {
        return submitStreamClientSide;
      } else if (request.spa.target.type === 'PRODUCTION') {
        return submitStreamEdgeDevice;
      } else {
        return submitStreamServerSide;
      }
    }
  }
  return _ => {};
}

export function submitBatchServerSide(request: SubmitRequest) {
  const runtimeType = getRuntimeType(request);
  request.setExecutionContext({
    type: runtimeType,
    images: request.values.images,
    application: request.application,
    spa: request.spa,
    streamDeploymentId: undefined,
  });
}

export function submitStreamServerSide(request: SubmitRequest) {
  const runtimeType = getRuntimeType(request);
  if (request.values.inferenceSource) {
    request.handleInferenceStart({
      application: request.application,
      spa: request.spa,
      inferenceSource: request.values.inferenceSource,
      streamSubject: request.streamSubject,
      streamingDevices: request.streamingDevices,
      ipCamera: request.values.ipCamera,
      onStartDeploy: (deployment: Deployment) => {
        if (request.values.inferenceSource) {
          request.setExecutionContext({
            type: runtimeType,
            inferenceSource: request.values.inferenceSource,
            streamingDevices: request.streamingDevices,
            ipCamera: request.values.ipCamera,
            application: request.application,
            spa: request.spa,
            streamDeploymentId: deployment.id,
          });
        }
      },
    });
  }
}

export function submitStreamEdgeDevice(request: SubmitRequest) {
  const runtimeType = getRuntimeType(request);
  const inputSourceType =
    request.values.inferenceSource === '' ? null : request.values.inferenceSource;
  const resourceId =
    inputSourceType === 'IP_CAMERA'
      ? request.values.ipCamera
      : inputSourceType === 'DEVICE_CAMERA'
      ? request.values.deviceCamera
      : null;
  if (request.spa.target.deploymentId && resourceId) {
    request.handleEdgeInferenceStart({
      deploymentId: request.spa.target.deploymentId,
      spaId: request.spa.id,
      inputSourceType: inputSourceType,
      inputSourceId: resourceId,
      onStartDeploy: () => {
        request.setExecutionContext({
          type: runtimeType,
          application: request.application,
          spa: request.spa,
        });
      },
      onError: e => {
        request.onError(e);
      },
    });
  }
}

export function submitBatchClientSide(request: SubmitRequest) {
  request.setExecutionContext({
    type: 'ONNX_RUNTIME_WEB',
    images: request.values.images,
    application: request.application,
    spa: request.spa,
    runtimeMode: request.values.runtimeMode,
    id: Date.now().toString(),
    inferenceSource: 'IMAGES',
    configuredParams: request.configuredParameters,
  });
}

export function submitStreamClientSide(request: SubmitRequest) {
  let onnxVideoURL = undefined;
  if (request.values.ipCamera) {
    let streamingDevice = request.streamingDevices.find(
      sd => sd.id === request.values.ipCamera
    );
    if (streamingDevice) {
      onnxVideoURL = streamingDevice.url;
    }
  }
  if (request.values.inferenceSource) {
    request.setExecutionContext({
      type: 'ONNX_RUNTIME_WEB',
      images: [],
      application: request.application,
      spa: request.spa,
      runtimeMode: request.values.runtimeMode,
      id: Date.now().toString(),
      inferenceSource: request.values.inferenceSource,
      onnxVideoURL,
      configuredParams: request.configuredParameters,
    });
  }
}

function getRuntimeType(request: SubmitRequest) {
  return request.application?.runtimeType === 'PY_PICASSO' ? 'PY_PICASSO' : 'OPENVX';
}
